import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogoSymbol, LogoWordmark } from "../components/Logo";
import { useAdminAuth } from "../lib/adminAuth";

export default function AdminLogin() {
  const { admin, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Auth check finished + already logged in → straight to dashboard
    if (admin && admin !== false) navigate("/admin/dashboard", { replace: true });
  }, [admin, navigate]);

  if (admin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fdf8f3] text-stone-500">
        Checking session…
      </div>
    );
  }
  if (admin && admin !== false) return <Navigate to="/admin/dashboard" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Access denied.");
    } finally { setSubmitting(false); }
  }

  return (
    <main
      data-testid="admin-login-page"
      className="flex min-h-screen items-center justify-center bg-[#fdf8f3] px-6 py-16"
    >
      <div className="w-full max-w-sm">
        <div className="mb-10 flex flex-col items-center gap-3">
          <LogoSymbol size={48} />
          <LogoWordmark size={20} />
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white/80 px-3 py-1 text-[11px] uppercase tracking-wider text-stone-600">
            <Lock className="h-3 w-3" /> Admin
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border border-stone-200 bg-white p-8 shadow-sm"
          data-testid="admin-login-form"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-xs uppercase tracking-wider text-stone-600">
                Email
              </Label>
              <Input
                id="admin-email"
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@blendsafe.com"
                autoComplete="email"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-xs uppercase tracking-wider text-stone-600">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="admin-password"
                  data-testid="admin-login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  data-testid="admin-login-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-stone-400 transition hover:text-stone-700"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <EyeOff className="h-4 w-4" />
                    : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-6 w-full"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</>
            ) : "Sign in"}
          </Button>

          <p className="mt-6 text-center text-[11px] text-stone-500">
            Restricted area · authorized personnel only
          </p>
        </form>
      </div>
    </main>
  );
}
